import React from "react";
import { createPortal } from "react-dom";
import { MdClose } from "react-icons/md";

/*
 * Kuest dialog (components/ui/dialog.tsx): bg-black/50 overlay, centred panel
 * max-w-lg rounded-lg border bg-background p-6 shadow-lg gap-4, scale-95 + fade,
 * close button top-right (rounded-md bg-accent p-2 opacity-70). Esc and the overlay
 * close it (unless `dismissible` is false), focus moves in on open, Tab stays inside,
 * and focus goes back to whatever had it before.
 */
const FOCUSABLE = 'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function Dialog({ open, onClose, title, description, label, children, className = "", dismissible = true, showClose = true, role = "dialog", initialFocusRef }) {
  const panelRef = React.useRef(null);
  const titleId = React.useId();
  const descId = React.useId();

  React.useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    const id = requestAnimationFrame(() => {
      const target = initialFocusRef?.current || panelRef.current?.querySelector("[autofocus]") || panelRef.current?.querySelector(FOCUSABLE) || panelRef.current;
      if (target) target.focus();
    });
    return () => {
      cancelAnimationFrame(id);
      document.body.style.overflow = overflow;
      if (previous && typeof previous.focus === "function") previous.focus();
    };
  }, [open, initialFocusRef]);

  if (!open) return null;

  function onKeyDown(event) {
    if (event.key === "Escape") {
      if (dismissible && onClose) {
        event.stopPropagation();
        onClose();
      }
      return;
    }
    if (event.key !== "Tab" || !panelRef.current) return;
    const nodes = Array.from(panelRef.current.querySelectorAll(FOCUSABLE));
    if (!nodes.length) return;
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" onKeyDown={onKeyDown}>
      <div
        className="wasaa-overlay fixed inset-0 bg-black/50"
        aria-hidden="true"
        onMouseDown={() => {
          if (dismissible && onClose) onClose();
        }}
      />
      <div
        ref={panelRef}
        role={role}
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={!title ? label : undefined}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={`wasaa-dialog relative grid max-h-[calc(100dvh-2rem)] w-full max-w-lg gap-4 overflow-y-auto rounded-lg border border-border bg-background p-6 text-foreground shadow-lg outline-none ${className}`}
      >
        {(title || description) && (
          <div className="flex flex-col gap-2 pr-8 text-left">
            {title && (
              <h2 id={titleId} className="text-lg font-semibold leading-none">
                {title}
              </h2>
            )}
            {description && (
              <p id={descId} className="text-sm text-muted-foreground">
                {description}
              </p>
            )}
          </div>
        )}
        {children}
        {showClose && onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-4 top-4 rounded-md bg-accent p-2 text-foreground opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
          >
            <MdClose className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}

export function DialogFooter({ className = "", children }) {
  return <div className={`flex flex-col-reverse gap-2 sm:flex-row sm:justify-end ${className}`}>{children}</div>;
}

export default Dialog;
